import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, combineLatest, defer, distinctUntilChanged, map, Observable, of, retry, shareReplay, Subject, Subscriber, takeUntil, tap, take, startWith, throwError } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { MqttUnavailableError } from '../mqtt-errors';
import { IMqttMessage, IMqttServiceOptions, IOnErrorEvent, MqttService } from 'ngx-mqtt';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { IDeviceState, IShadowState, IUnit } from '../interfaces/interfaces';
import { MessagesState, MqttMessageStorageService } from './mqtt-message-storage.service';
import { DeviceCacheService } from './device-cache.service';
import { DeviceRegistryService } from 'src/app/services/device-registry.service';
import { Platform } from '@ionic/angular';
import { App } from '@capacitor/app';
import { UserStateService } from 'src/app/shared/services/user-state.class';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { FRONT_DOOR_LOCK, FRONT_DOOR_LOCK_JAMMED } from '../actions-list';
import { newCommandMeta, readCid, MQTT_TELEMETRY_WIDGET, CommandStage, PUBLISH_ACK_WATCHDOG_MS, entityFromShadowTopic } from '../command-telemetry';

const IOT_ENDPOINT = environment.iotEndpoint;
const CUSTOM_AUTHORIZER = environment.iotMqttUsername;
const INITIAL_RECONNECT_DELAY = 1000; // 1 second
const MAX_RECONNECT_DELAY = 30000; // 30 seconds
const RECONNECT_LADDER_THRESHOLD = 3; // force a token re-acquire every Nth failed reconnect
const PUBLISH_RETRY_ATTEMPTS = 3;   // transient publish errors retried before failing the tap
const PUBLISH_RETRY_DELAY_MS = 400; // backoff between publish retries
const COMMAND_STALENESS_MS = 30000; // queued commands older than this are dropped, never delivered

@Injectable({
  providedIn: 'root'
})
export class MqttConnectorService implements OnDestroy {
  private isUnitsLoaded$: BehaviorSubject<boolean>;
  private isMqttConnected: boolean = false;
  private isConnecting: boolean = false;
  private client: MqttService | undefined;
  private unsubscribe$!: Subject<void>;
  private unitGuid?: string;

  /** The current unit's AWS IoT thing id — a stable per-household identifier
   *  (undefined until the devices/units response has resolved). Read-only view
   *  for consumers that need to tag data by household (e.g. analytics). */
  public get unitId(): string | undefined {
    return this.unitGuid;
  }

  private reconnectTimer: any;
  private lastToken?: string;
  private initialConnectionAttempted: boolean = false;
  private connectionAttemptCount: number = 0;
  private connectionStatus$ = new BehaviorSubject<'disconnected' | 'connecting' | 'connected'>('disconnected');
  private stableConnectionTimer: any;
  private isDevicesFetchInProgress: boolean = false;
  private pendingCommands: Array<{ entityId: string; message: any; enqueuedAt: number; observer: Subscriber<void>; timer: any; failReason: string; errCode: 'disconnected' | 'no_unit' }> = [];

  /** Requests awaiting an add-on reply, keyed by action. The reply topic
   *  (<prefix>/<thing>/api/response) is per-UNIT, not per-resident — every
   *  roommate's client is subscribed to it. The add-on's response_to carries
   *  no request id, so matching on action alone let one resident's reply
   *  resolve another resident's pending request (and, for get_resident_scenes,
   *  durably write their scenes into the wrong account). Each entry now also
   *  records the cognito_sub the request was sent with; a reply is matched to
   *  the oldest pending entry for that action whose recorded sub equals the
   *  reply's cognito_sub. A reply that matches no entry is ignored.
   *
   *  Replies with no cognito_sub — today, only error replies, which are
   *  {response_to, error} with no other fields — fall back to resolving the
   *  oldest pending entry for the action regardless of its recorded sub. This
   *  also covers requests sent with no cognito_sub at all (sub undefined on
   *  both sides). This is deliberate, not an oversight: an error payload
   *  carries no per-resident data to leak, and the add-on has no way to say
   *  "this error belongs to resident X" — refusing to correlate it would just
   *  turn every backend error (e.g. an unknown scene id) into a generic
   *  timeout for the caller, breaking the client's error-driven fallbacks. */
  private pendingApiRequests = new Map<string, Array<{
    resolve: (v: any) => void; reject: (e: Error) => void; timer: any; sub: string | undefined;
  }>>();

  constructor(
    private authService: AuthService,
    private httpClient: HttpClient,
    private mqttService: MqttService,
    private storage: MqttMessageStorageService,
    private deviceCache: DeviceCacheService,
    private deviceRegistry: DeviceRegistryService,
    private platform: Platform,
    private router: Router,
    private userStateService: UserStateService,
    private analyticsService: AnalyticsService,
  ) {
    this.client = this.mqttService;
    this.isUnitsLoaded$ = new BehaviorSubject<boolean>(false);

    this.setupAppLifecycleHandling();

    // Monitor authentication and IoT token availability
    console.log('[MQTT] Setting up authentication monitoring');
    combineLatest([
      this.authService.getFullyAuthenticated(),
      this.authService.hasIotToken()
    ]).subscribe(([isFullyAuthenticated, hasToken]) => {
      console.log('[MQTT] Auth state changed:', {
        isFullyAuthenticated,
        hasToken,
        isMqttConnected: this.isMqttConnected,
        isConnecting: this.isConnecting
      });

      if (isFullyAuthenticated && hasToken && !this.isMqttConnected && !this.isConnecting) {
        console.log('[MQTT] User is fully authenticated with IoT token, initiating MQTT connection');
        this.connect();
      } else if (!isFullyAuthenticated && this.isMqttConnected) {
        console.log('[MQTT] User logged out, disconnecting MQTT');
        this.closeConnection();
      } else if (isFullyAuthenticated && !hasToken && !this.isConnecting) {
        console.log('[MQTT] User is fully authenticated but IoT token is missing - waiting for token');
      }
    });
  }

  private setupAppLifecycleHandling() {
    // Handle app going to background
    App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        // App went to background - we keep the connection alive
        // console.log('App going to background - keeping MQTT connection alive');
      } else {
        // App came to foreground - ensure connection
        this.handleAppForeground();
      }
    });

    // Handle platform resume
    this.platform.resume.subscribe(() => {
      this.handleAppForeground();
    });

    // Handle platform pause
    this.platform.pause.subscribe(() => {
      // Keep connection alive during pause
      // console.log('Platform paused - keeping MQTT connection alive');
    });
  }


  private handleAppForeground() {
    // Refresh the IoT token on resume before (re)connecting, so we never try to
    // connect with a token that expired while the app was backgrounded.
    this.authService.ensureFreshToken()
      .catch(() => { /* surfaced via needsReauth$ */ })
      .finally(() => {
        if (!this.isMqttConnected && this.initialConnectionAttempted && !this.isConnecting) {
          this.connect();
        }
      });
  }

  public connect() {
    // Skip if already connected or connection in progress
    if (this.isMqttConnected || this.isConnecting) {
      console.log('[MQTT] Already connected or connection in progress, skipping connect', {
        isMqttConnected: this.isMqttConnected,
        isConnecting: this.isConnecting
      });
      return;
    }

    console.log('[MQTT] ===== STARTING CONNECTION PROCESS =====');
    this.isConnecting = true;
    this.connectionStatus$.next('connecting');

    // Refresh the token before connecting (acquires/rotates if missing or near
    // expiry, self-healing via /refresh) so we never hand a dead token to the
    // custom authorizer.
    console.log('[MQTT] Ensuring fresh IoT token');
    this.authService.ensureFreshToken().catch(() => { /* surfaced via needsReauth$ */ }).then(() => {
      this.authService.getIotToken().pipe(
        take(1)
      ).subscribe(token => {
        if (!token) {
          this.isConnecting = false;
          this.connectionStatus$.next('disconnected');
          this.initialConnectionAttempted = true;
          if (this.authService.getNeedsReauthValue()) {
            // Every automated recovery path is exhausted; wait for the resident
            // to sign in again (which retriggers connect) instead of hammering
            // the login/refresh endpoints in a tight loop.
            console.log('[MQTT] No IoT token and re-auth required - awaiting sign-in');
          } else {
            console.log('[MQTT] No IoT token available yet, scheduling retry');
            this.scheduleReconnect(5000);
          }
          return;
        }

        // Token exists, proceed with connection
        console.log('[MQTT] IoT token available, starting MQTT connection', {
          tokenLength: token.length,
          tokenPreview: token.substring(0, 10) + '...' + token.substring(token.length - 10)
        });
        this.lastToken = token;
        this.initialConnectionAttempted = true;
        this.startConnection(token);
      });
    }).catch(error => {
      console.error('[MQTT] Error ensuring token loaded:', error);
      this.isConnecting = false;
      this.connectionStatus$.next('disconnected');
    });
  }

  public getMessageListener(entityId?: string): Observable<IShadowState | undefined> {
    if (!entityId) return of();
    return this.storage.items$.pipe(
      map((state: MessagesState) => state[entityId])
    )
  }

  public getConnectionStatus(): Observable<'disconnected' | 'connecting' | 'connected'> {
    return this.connectionStatus$.asObservable();
  }

  /**
   * Single signal for control widgets: 'ready' when we can publish, 'needs_reauth'
   * when the resident must sign in again (every automated recovery exhausted),
   * else 'reconnecting'. Widgets gate taps and show the matching state.
   */
  public getControlAvailability(): Observable<'ready' | 'reconnecting' | 'needs_reauth'> {
    return combineLatest([
      this.connectionStatus$,
      this.authService.getNeedsReauth(),
    ]).pipe(
      map(([status, needsReauth]) =>
        needsReauth ? 'needs_reauth' : status === 'connected' ? 'ready' : 'reconnecting'),
      distinctUntilChanged(),
    );
  }

  public listenToEntities(...entityIds: string[]): Observable<(IShadowState | undefined)[]> {
    if (!entityIds.length) {
      return of([]);
    }
    return combineLatest(
      entityIds.map(id => this.getMessageListener(id).pipe(startWith(undefined)))
    );
  }

  public isDataLoaded(): Observable<boolean> {
    // Check if we have any data in storage (from cache or MQTT)
    return combineLatest([
      this.storage.items$.pipe(
        map((state: MessagesState) => Object.keys(state).length > 0),
      ),
      this.deviceCache.isCacheLoaded(),
      this.isUnitsLoaded$.asObservable()
    ]).pipe(
      map(([hasStorageData, cacheLoaded, unitsLoaded]) => {
        // If we have cached data, show the homepage immediately
        if (cacheLoaded && hasStorageData) {
          return true;
        }
        // If no cache but units are loaded via MQTT, also show homepage
        if (unitsLoaded) {
          return true;
        }
        // Otherwise, keep showing loading
        return false;
      }),
      distinctUntilChanged()
    );
  }

  private publishWithRetry(entityId: string, message: any, cid: string | undefined = readCid(message)): Observable<void> {
    const publishTopic = `$aws/things/${this.unitGuid}/shadow/name/${entityId.replace(".", "-")}/update`;
    const start = Date.now();
    // defer() re-checks isMqttConnected on every retry resubscription, not just the
    // first, so a dropped connection can't cause a retry to write to a dead socket.
    const publishOnce = () => (this.isMqttConnected && this.client)
      ? this.client.publish(publishTopic, JSON.stringify(message), { qos: 1 })
      : throwError(() => new MqttUnavailableError('disconnected'));
    return new Observable<void>(observer => {
      const watchdog = setTimeout(() => {
        this.analyticsService.trackWidgetInteraction(
          MQTT_TELEMETRY_WIDGET, CommandStage.PUBLISH_NO_ACK, { cid, entityId, unit: this.unitGuid }
        );
      }, PUBLISH_ACK_WATCHDOG_MS);
      const sub = defer(publishOnce).pipe(
        retry({ count: PUBLISH_RETRY_ATTEMPTS, delay: PUBLISH_RETRY_DELAY_MS })
      ).subscribe({
        next: () => {
          clearTimeout(watchdog);
          this.analyticsService.trackWidgetInteraction(
            MQTT_TELEMETRY_WIDGET, CommandStage.PUBLISH_ACKED,
            { cid, entityId, latencyMs: Date.now() - start, unit: this.unitGuid }
          );
          observer.next();
          observer.complete();
        },
        error: (err) => {
          clearTimeout(watchdog);
          console.error(`[MQTT] Publish failed after ${PUBLISH_RETRY_ATTEMPTS} retries to ${publishTopic}:`, err);
          this.analyticsService.trackWidgetInteraction('mqtt', 'control_failure', { topic: publishTopic, entityId, cid, unit: this.unitGuid });
          observer.error(err);
        }
      });
      return () => {
        clearTimeout(watchdog);
        sub?.unsubscribe();
      };
    });
  }

  public postMessage(entityId: string, message: any): Observable<void> {
    const meta = newCommandMeta();
    if (message?.state?.desired && typeof message.state.desired === 'object') {
      message.state.desired._meta = meta;
    }
    this.analyticsService.trackWidgetInteraction(
      MQTT_TELEMETRY_WIDGET, CommandStage.INITIATED, { cid: meta.cid, entityId, unit: this.unitGuid }
    );
    if (this.isMqttConnected && this.unitGuid?.length) {
      return this.publishWithRetry(entityId, message, meta.cid);
    }

    // Not publish-ready (connection down and/or unit not resolved yet): queue; flushed on reconnect/unitGuid-resolve, dropped if stale.
    // Stamp why at enqueue time so an aged-out command keeps that distinction (unit-never-resolved vs network-down) in the funnel and the error.
    const failReason = this.isMqttConnected ? 'unavailable' : 'stale';
    const errCode: 'disconnected' | 'no_unit' = this.isMqttConnected ? 'no_unit' : 'disconnected';
    return new Observable<void>(observer => {
      const entry = { entityId, message, enqueuedAt: Date.now(), observer, timer: null as any, failReason, errCode };
      entry.timer = setTimeout(() => {
        this.pendingCommands = this.pendingCommands.filter(e => e !== entry);
        this.analyticsService.trackWidgetInteraction('mqtt', 'control_failure', { reason: entry.failReason, entityId, cid: meta.cid, unit: this.unitGuid });
        observer.error(new MqttUnavailableError(entry.errCode));
      }, COMMAND_STALENESS_MS);
      this.pendingCommands.push(entry);
      if (!this.isConnecting && !this.isMqttConnected) {
        this.connect();
      } else if (this.isMqttConnected && !this.unitGuid?.length) {
        // Connected but the devices fetch never resolved a unit; re-trigger it so the queue can't just sit until it goes stale. isDevicesFetchInProgress guards a double fetch.
        // Resolve the token the same way connect() does. Replaying this.lastToken here is the dead-token loop scheduleReconnect warns about.
        this.authService.ensureFreshToken()
          .catch(() => { /* surfaced via needsReauth$ */ })
          .then(() => this.authService.getIotToken().pipe(take(1))
            .subscribe(token => this.fetchAndSubscribeToThings(token, true)));
      }
      return () => {
        clearTimeout(entry.timer);
        this.pendingCommands = this.pendingCommands.filter(e => e !== entry);
      };
    });
  }

  /**
   * On reconnect, deliver any commands queued while disconnected. Entries are
   * re-checked for staleness here rather than trusting their own eviction
   * timer to have already fired — on a real WebView that setTimeout can be
   * throttled while backgrounded and fire late relative to this flush path.
   */
  private flushPendingCommands(): void {
    if (!this.isMqttConnected || !this.unitGuid?.length || !this.pendingCommands.length) {
      return;
    }
    const now = Date.now();
    const queued = this.pendingCommands;
    this.pendingCommands = [];
    for (const e of queued) {
      clearTimeout(e.timer);
      if (now - e.enqueuedAt > COMMAND_STALENESS_MS) {
        this.analyticsService.trackWidgetInteraction('mqtt', 'control_failure', { reason: e.failReason, entityId: e.entityId, cid: readCid(e.message), unit: this.unitGuid });
        e.observer.error(new MqttUnavailableError(e.errCode));
        continue;
      }
      this.publishWithRetry(e.entityId, e.message).subscribe({
        next: () => { e.observer.next(); e.observer.complete(); },
        error: (err) => e.observer.error(err),
      });
    }
  }

  public publishShadowGet(entityId: string): Observable<void> {
    if (this.isMqttConnected && this.unitGuid?.length) {
      const publishTopic = `$aws/things/${this.unitGuid}/shadow/name/${entityId.replace(".", "-")}/get`;
      return new Observable<void>(observer => {
        this.client?.publish(publishTopic, JSON.stringify({}), { qos: 1 }).subscribe({
          next: () => {
            observer.next();
            observer.complete();
          },
          error: (err) => {
            console.error(`Error publishing shadow get request to ${publishTopic}:`, err);
            observer.error(err);
          }
        });
      });
    }
    // Shadow-get is a best-effort read (callers are isMqttConnected-guarded and
    // it re-runs on reconnect), so stay a silent no-op here rather than throwing.
    return of();
  }

  public setStateValue(entityId: string, desiredState: any): Observable<void> {
    const payload = {
      state: {
        desired: {
          state: desiredState,
        }
      }
    }
    return this.postMessage(entityId, payload)
  }

  /**
   * Trigger a Home Assistant script by name, optionally passing `variables` that the
   * iot-core bridge forwards to script.turn_on. Publishes ONE shadow desired update
   * carrying both state:'on' and (optionally) attributes.variables so the bridge sees
   * them together — chaining setStateValue + setAttributeValue would send two separate
   * deltas. postMessage() expects a fully-wrapped { state: { desired } } payload (and
   * stamps its telemetry _meta onto state.desired), so we build that shape here.
   * @param scriptName object_id only, underscores (e.g. 'apply_scene') -> entity script.<name>
   */
  public triggerScript(scriptName: string, variables?: Record<string, any>): Observable<void> {
    const entityId = `script.${scriptName}`;
    const desired: { state: string; attributes?: { variables: Record<string, any> } } = { state: 'on' };
    if (variables) { desired.attributes = { variables }; }
    return this.postMessage(entityId, { state: { desired } });
  }

  public setAttributeValue(entityId: string, desiredAttributes: {
    [key: string]: any,
  }): Observable<void> {
    const payload = {
      state: {
        desired: {
          attributes: {
            ...desiredAttributes
          }
        }
      }
    }
    return this.postMessage(entityId, payload)
  }

  public setInputTextValue(entityId: string, value: string): Observable<void> {
    const payload = {
      state: {
        desired: {
          state: value
        }
      }
    }
    return this.postMessage(entityId, payload)
  }

  public setInputBooleanValue(entityId: string, value: boolean): Observable<void> {
    const payload = {
      state: {
        desired: {
          state: value ? 'on' : 'off'
        }
      }
    }
    return this.postMessage(entityId, payload).pipe(
      tap(() => console.log(`Setting ${entityId} to ${value ? 'on' : 'off'}`)),
      catchError(error => {
        console.error(`Error setting ${entityId}:`, error);
        this.analyticsService.trackWidgetInteraction('mqtt', 'control_failure', { entityId, unit: this.unitGuid });
        return of();
      })
    );
  }

  public setInputNumberValue(entityId: string, value: number): Observable<void> {
    const payload = {
      state: {
        desired: {
          state: value.toString()
        }
      }
    }
    return this.postMessage(entityId, payload)
  }

  /**
   * Publish an action envelope to <prefix>/<unit_id>/api.
   *
   * The prefix is PER-ENVIRONMENT ("ha" on production, "hadev" on develop) and must
   * come from the environment, never a literal: the IoT custom authorizer grants
   * iot:Publish on exactly `topic/<api_mqtt_prefix>/<thing>/api` and nothing wider,
   * so a mismatched prefix is rejected by the broker with "Not authorized" rather
   * than failing anywhere visible. Shadow-based commands ($aws/things/...) are
   * granted separately and keep working, which is what hid this on develop.
   *
   * @param action The api-topic action name (e.g. 'call_ha_action', 'save_resident_scenes')
   * @param data Payload for the action
   * @param analyticsLabel Label used in logs/analytics for this call; defaults to `action`.
   *   callHaAction passes `<domain>.<service>` here to keep its existing, more specific label.
   */
  public publishApiCommand(action: string, data: Record<string, any> = {}, analyticsLabel: string = action): Observable<void> {
    // defer() re-checks isMqttConnected/unitGuid at subscription time (see VI-533's
    // publishWithRetry fix) rather than once at call time, so a connection dropped
    // between call and subscription can't write to a torn-down mqtt.js stream.
    return defer(() => {
      if (this.isMqttConnected && this.unitGuid?.length) {
        const topic = `${environment.iotMqttPrefix}/${this.unitGuid}/api`;
        const payload = { action, data };

        console.log(`[MQTT] Publishing ${analyticsLabel} on topic ${topic}`);

        return new Observable<void>(observer => {
          this.client?.publish(topic, JSON.stringify(payload), { qos: 1 }).subscribe({
            next: () => {
              console.log(`[MQTT] Successfully published ${analyticsLabel}`);
              observer.next();
              observer.complete();
            },
            error: (err) => {
              console.error(`[MQTT] Error publishing ${analyticsLabel}:`, err);
              this.analyticsService.trackWidgetInteraction('mqtt', 'control_failure', { action: analyticsLabel, unit: this.unitGuid });
              observer.error(err);
            }
          });
        });
      }
      console.warn(`[MQTT] Cannot publish ${analyticsLabel}: MQTT not connected or unitGuid missing`);
      this.analyticsService.trackWidgetInteraction('mqtt', 'control_failure', { reason: 'unavailable', action: analyticsLabel, unit: this.unitGuid });
      return throwError(() => new MqttUnavailableError(this.unitGuid?.length ? 'disconnected' : 'no_unit'));
    });
  }

  /**
   * Call a Home Assistant service/action via MQTT.
   * Publishes to <prefix>/<unit_id>/api topic with call_ha_action format — see
   * publishApiCommand for the prefix/authorizer contract this relies on.
   *
   * @param domain The HA domain (e.g., 'pyscript', 'light', 'script')
   * @param service The service name (e.g., 'holiday_light_sync_start')
   * @param serviceData Optional data to pass to the service
   */
  public callHaAction(domain: string, service: string, serviceData: Record<string, any> = {}): Observable<void> {
    return this.publishApiCommand(
      'call_ha_action',
      { domain, service, service_data: serviceData },
      `${domain}.${service}`,
    );
  }

  /**
   * Publish an api command and wait for the add-on's reply on
   * <prefix>/<thing>/api/response (see subscribeToShadowTopics). Resolves with
   * the `response` payload on success, rejects with an Error on an `error`
   * reply or if no reply arrives within timeoutMs. The reply is correlated by
   * (action, cognito_sub) — see pendingApiRequests' doc comment; that topic is
   * shared by every resident of the unit, so passing a `cognito_sub` in `data`
   * (as the resident-scenes commands do) is what keeps a roommate's reply from
   * resolving this request.
   */
  public requestApiCommand(action: string, data: Record<string, any> = {}, timeoutMs = 10000): Promise<any> {
    return new Promise((resolve, reject) => {
      const sub: string | undefined = data?.['cognito_sub'];
      const entry = {
        resolve, reject, sub,
        timer: setTimeout(() => {
          this.removePending(action, entry);
          reject(new Error(`Timed out waiting for ${action}`));
        }, timeoutMs),
      };
      const queue = this.pendingApiRequests.get(action) ?? [];
      queue.push(entry);
      this.pendingApiRequests.set(action, queue);

      this.publishApiCommand(action, data).pipe(take(1)).subscribe({
        error: (err) => { this.removePending(action, entry); clearTimeout(entry.timer); reject(err); },
      });
    });
  }

  private removePending(action: string, entry: any): void {
    const queue = this.pendingApiRequests.get(action);
    if (!queue) { return; }
    const i = queue.indexOf(entry);
    if (i >= 0) { queue.splice(i, 1); }
    if (!queue.length) { this.pendingApiRequests.delete(action); }
  }

  private handleApiResponse(payload: { response_to?: string; response?: any; error?: string; cognito_sub?: string }): void {
    const action = payload?.response_to;
    if (!action) { return; }
    const queue = this.pendingApiRequests.get(action);
    if (!queue?.length) { return; }
    // See pendingApiRequests' doc comment: a reply with a sub only matches the
    // oldest pending entry recorded with that same sub; a reply with no sub
    // (error replies today) falls back to the oldest pending entry for the
    // action, whatever sub it was recorded with.
    const replySub = payload.cognito_sub;
    const entry = replySub === undefined ? queue[0] : queue.find(e => e.sub === replySub);
    if (!entry) { return; } // no matching pending request for this reply — ignore it, don't resolve someone else's
    this.removePending(action, entry);
    clearTimeout(entry.timer);
    if (payload.error) { entry.reject(new Error(payload.error)); }
    else { entry.resolve(payload.response); }
  }

  /**
   * Fresh clientId per connection. AWS IoT terminates the older of two
   * connections sharing a clientId (DUPLICATE_CLIENTID); reusing a fixed id
   * across reconnects let a single device kick itself in an infinite loop.
   * A unique id per attempt means an orphaned session times out harmlessly
   * instead of colliding. Authorizer policy allows any clientId (iot:Connect
   * Resource "*").
   */
  private generateClientId(): string {
    const nonce = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    return `vessel-${nonce}`;
  }

  private getConnection(token: string): IMqttServiceOptions {
    return {
      protocol: "wss",
      username: CUSTOM_AUTHORIZER,
      hostname: IOT_ENDPOINT,
      password: token,
      port: 443,
      path: "/mqtt",
      protocolVersion: 5,
      clientId: this.generateClientId(),
      // App owns reconnection via scheduleReconnect(); disable ngx-mqtt/mqtt.js
      // internal auto-reconnect (default 10000ms) so two reconnect drivers can't
      // race and open overlapping same-clientId connections.
      reconnectPeriod: 0,
      // Defense-in-depth for the fresh-clientId model: a short keepalive (vs
      // mqtt.js default 60s) makes the broker evict an orphaned/half-open
      // session faster, and clean:true starts a fresh session with no queued
      // state carried across the now-unique clientIds.
      keepalive: 30,
      clean: true,
    }
  }

  public ngOnDestroy(): void {
    this.closeConnection();
  }

  private startConnection(token?: string) {
    if (!token) {
      this.isConnecting = false;
      console.log('[MQTT] Cannot start MQTT connection - no token provided');
      return;
    }

    console.log('[MQTT] ===== STARTING MQTT CONNECTION =====');
    this.unsubscribe$ = new Subject<void>();

    try {
      const tokenPreview = token.substring(0, 10) + '...' + token.substring(token.length - 10);
      console.log('[MQTT] Connection details:', {
        protocol: "wss",
        username: CUSTOM_AUTHORIZER,
        hostname: IOT_ENDPOINT,
        passwordLength: token.length,
        passwordPreview: tokenPreview,
        port: 443,
        path: "/mqtt",
        protocolVersion: 5,
      });

      // Create a listener for global WebSocket errors that could affect MQTT
      const wsErrorListener = (event: Event) => {
        console.error('[MQTT] WebSocket error event detected:', event);
      };

      // Add listener before connection attempt
      window.addEventListener('error', wsErrorListener);

      const connectionOptions = this.getConnection(token);
      console.log('[MQTT] Calling client.connect() with options:', {
        protocol: connectionOptions.protocol,
        hostname: connectionOptions.hostname,
        port: connectionOptions.port,
        path: connectionOptions.path,
        username: connectionOptions.username,
        passwordLength: connectionOptions.password?.length || 0
      });

      // Connection attempt
      this.client?.connect(connectionOptions);
      console.log('[MQTT] client.connect() called successfully');

      // Remove listener after a short delay
      setTimeout(() => {
        window.removeEventListener('error', wsErrorListener);
      }, 5000);
    } catch (error: any) {
      console.error('[MQTT] Error during MQTT connection setup:', error);
      this.handleConnectionError(error);
      return;
    }

    console.log('[MQTT] Setting up error handling');
    this.setupErrorHandling();

    console.log('[MQTT] Subscribing to onConnect observable');
    this.client?.onConnect.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      console.log('[MQTT] ===== CONNECTION SUCCESSFUL =====');
      this.isMqttConnected = true;
      this.isConnecting = false;
      this.connectionStatus$.next('connected');
      this.analyticsService.trackMqttHealth('connected');
      this.clearReconnectTimer();

      // Don't reset connection attempt count immediately - wait for stable connection
      // This prevents infinite reconnect loops when connection succeeds but immediately fails
      this.clearStableConnectionTimer();
      this.stableConnectionTimer = setTimeout(() => {
        if (this.isMqttConnected) {
          // console.log('[MQTT] Connection stable for 30 seconds, resetting attempt counter');
          this.connectionAttemptCount = 0;
        }
      }, 30000); // Reset counter only after 30 seconds of stable connection

      // console.log('[MQTT] Connection established, checking for cached data');
      // Check if we have cached data already loaded
      // DeviceCacheService initializes itself in its constructor
      let hasCachedData = false;

      // Check if message storage already has data
      this.storage.items$.pipe(take(1)).subscribe(state => {
        hasCachedData = Object.keys(state || {}).length > 0;
        // console.log('[MQTT] Cached data check:', {
        //   hasCachedData,
        //   cachedKeysCount: Object.keys(state || {}).length
        // });

        if (hasCachedData) {
          // console.log('[MQTT] Using cached data for immediate display');
          // If we have cached data, mark data as loaded initially
          this.isUnitsLoaded$.next(true);
          // Note: Priority shadow requests are now made after devices are fetched
          // to ensure the correct unitGuid is used (from devices response, not GUID endpoint)
        }
      });

      // Fetch device data using IoT token (no Cognito token needed - /thing endpoint removed)
      // console.log('[MQTT] Fetching device data');
      this.fetchAndSubscribeToThings(token as string | undefined, hasCachedData);
    });
  }
  
  private fetchAndSubscribeToThings(iotToken: string | undefined, hasCachedData: boolean): void {
    // Guard against concurrent fetches to prevent API throttling
    if (this.isDevicesFetchInProgress) {
      console.log('[MQTT] Device fetch already in progress, skipping');
      return;
    }
    this.isDevicesFetchInProgress = true;

    console.log('[MQTT] fetchAndSubscribeToThings called:', {
      hasIotToken: !!iotToken,
      hasCachedData
    });

    this.getThings(iotToken)
      .subscribe({
        next: (units: IUnit[] | undefined) => {
          this.isDevicesFetchInProgress = false;
          if (!units?.length) {
            console.log('[MQTT] No units returned from getThings');
            // getThings() swallows HTTP errors into of(undefined), so a failed
            // units-fetch on reconnect lands here, not in the error: branch below.
            // MQTT is already up and unitGuid may still hold from a prior
            // successful fetch — flush is internally guarded, safe to attempt.
            this.flushPendingCommands();
            return;
          }

          console.log('[MQTT] Processing', units.length, 'units');
          console.log('[MQTT] Had cached data:', hasCachedData);

          // First subscribe to general shadow topics
          console.log('[MQTT] Subscribing to shadow topics for', units.length, 'units');
          units.forEach(unit => {
            this.subscribeToShadowTopics(unit);
          });

          // ALWAYS initialize states with fresh data from the API
          // This updates any stale cached values with current device states
          console.log('[MQTT] Initializing listener states');
          this.initListenersState(units);

          // Reconnect just re-established connection and set unitGuid — deliver
          // any commands the resident issued while we were down.
          this.flushPendingCommands();

          // If we have cached data, mark as loaded immediately so UI can render
          // Users can start controlling devices right away with cached state
          if (hasCachedData) {
            console.log('[MQTT] Using cached data - marking units as loaded immediately');
            this.isUnitsLoaded$.next(true);
          }

          // Request shadow updates after a short delay for subscriptions to establish
          // MQTT subscriptions typically complete in <50ms, 150ms is conservative
          setTimeout(() => {
            // Request priority device shadows for immediate UI updates
            this.requestPriorityDeviceShadows();

            // Request latest shadow states for all priority devices
            if (units?.length) {
              this.requestLatestShadowStates(units);
            }

            // Mark as loaded if we didn't have cached data
            if (!hasCachedData) {
              this.isUnitsLoaded$.next(true);
            }

            // Update cache with fresh data
            this.deviceCache.cacheDeviceStates(units);
          }, 150); // Reduced from 500ms - subscriptions establish much faster
        },
        error: (error) => {
          this.isDevicesFetchInProgress = false;
          console.error('[MQTT] Error in fetchAndSubscribeToThings:', error);
          // getThings() catches HTTP errors internally and never emits error:,
          // so this branch is effectively unreachable for units-fetch failures;
          // kept for defense against unexpected upstream throws.
        }
      });
  }
    
  private setupErrorHandling(): void {
    // console.log('[MQTT] Setting up error handlers');
    this.client?.onError.pipe(takeUntil(this.unsubscribe$)).subscribe((errorEvent: IOnErrorEvent) => {
      console.error('[MQTT] ===== CONNECTION ERROR =====');
      console.error('[MQTT] Error message:', errorEvent.message);
      
      // Get more information if possible - safely accessing potentially undefined properties
      try {
        const errorDetails = JSON.stringify(errorEvent);
        console.error('[MQTT] Error details:', errorDetails);
      } catch (e) {
        console.error('[MQTT] Could not stringify error event');
      }
      
      this.isConnecting = false;
      this.analyticsService.trackMqttHealth('error', {
        message: errorEvent.message?.substring(0, 100)
      });

      // Check for "Connection refused: Not authorized" error - this means token is invalid
      const isNotAuthorizedError = errorEvent.message?.includes('Connection refused: Not authorized') ||
                                   errorEvent.message?.includes('Not authorized') ||
                                   errorEvent.message?.includes('Not Authorized');
      
      // On an auth-type error, clear the dead token so the reconnect's
      // ensureFreshToken() re-acquires via the self-heal ladder (login ->
      // /refresh). We no longer force-navigate to houseless; recovery is
      // automatic, and if it can't be (permanent), needsReauth$ drives the
      // per-widget "sign in again" prompt instead of a full-screen redirect.
      if (errorEvent.message?.includes('401') || errorEvent.message?.includes('403') ||
          errorEvent.message?.includes('Unauthorized') || errorEvent.message?.includes('Forbidden') ||
          errorEvent.message?.includes('Authentication') || errorEvent.message?.includes('authentication') ||
          isNotAuthorizedError) {
        console.log('[MQTT] Authentication error detected, clearing IoT token for re-acquire');
        this.authService.clearIotToken();
      } else {
        console.log('[MQTT] Non-auth error, keeping IoT token cached');
      }

      this.handleConnectionError(errorEvent);
    });

    this.client?.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      console.log('[MQTT] ===== CONNECTION CLOSED =====');
      this.isConnecting = false;
      this.analyticsService.trackMqttHealth('disconnected');
      // Don't clear token on normal connection close - this happens during backgrounding
      // Token should only be cleared on authentication failures
      console.log('[MQTT] Connection closed - keeping IoT token cached');
      this.handleConnectionError(new Error('Connection closed'));
    });

    // console.log('[MQTT] Error handlers setup complete');
  }

  private handleConnectionError(error: any) {
    console.error('MQTT connection error:', error.message || JSON.stringify(error));
    this.connectionStatus$.next('disconnected');
    this.closeConnection();

    if (this.initialConnectionAttempted) {
      this.connectionAttemptCount++;

      // Repeated failures can mean the broker is rejecting a token our local
      // clock still considers valid (revoked, clock skew, or a close with no
      // error message). ensureFreshToken() alone would skip it, so force a
      // re-mint through the ladder every Nth attempt. If that comes back
      // permanent, needsReauth$ is set and connect() stops the blind loop.
      if (this.connectionAttemptCount % RECONNECT_LADDER_THRESHOLD === 0) {
        console.log('[MQTT] Repeated reconnect failures - forcing token re-acquire via ladder');
        this.authService.acquireIotToken('mqtt-repeated-failure').catch(() => { /* needsReauth$ */ });
      }

      const reconnectDelay = Math.min(
        INITIAL_RECONNECT_DELAY * Math.pow(2, this.connectionAttemptCount - 1),
        MAX_RECONNECT_DELAY
      );
      console.log(`Scheduling reconnect in ${reconnectDelay/1000} seconds (attempt #${this.connectionAttemptCount})`);
      this.scheduleReconnect(reconnectDelay);
      this.analyticsService.trackMqttHealth('reconnect_scheduled', {
        attempt: this.connectionAttemptCount,
        delayMs: reconnectDelay
      });
    } else {
      console.log('Skipping reconnect as initial connection has not been attempted yet');
    }
  }

  private scheduleReconnect(delay: number) {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      console.log('Attempting to reconnect MQTT...');
      // Always go through connect() so we re-fetch the current (possibly rotated)
      // token. Replaying this.lastToken was the bug that looped forever on a dead
      // token after expiry.
      this.connect();
    }, delay);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
  }

  private clearStableConnectionTimer() {
    if (this.stableConnectionTimer) {
      clearTimeout(this.stableConnectionTimer);
      this.stableConnectionTimer = undefined;
    }
  }

  private closeConnection() {
    console.log('[MQTT] Closing MQTT connection');
    try {
      this.clearReconnectTimer();
      this.clearStableConnectionTimer();
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
      this.client?.disconnect();
      this.isMqttConnected = false;
      this.isConnecting = false;
      this.connectionStatus$.next('disconnected');
    } catch (error: any) {
      console.error('[MQTT] Error closing MQTT connection:', error.message || JSON.stringify(error));
      this.isConnecting = false;
    }
  }

  /**
   * Subscribe to shadow state topics for a specific unit
   * @param unit The unit to subscribe to
   */
  private subscribeToShadowTopics(unit: IUnit): void {
    if (!this.isMqttConnected) {
      return;
    }

    // Subscribe to shadow state updates (wildcard subscription)
    const updateTopic = `$aws/things/${unit.unit_id}/shadow/name/+/update`;
    console.log('[MQTT] Subscribing to topic:', updateTopic);

    this.client?.observe(updateTopic).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (message: IMqttMessage) => {
        const parsed: IShadowState | undefined = this.parseMessage(message);
        if (parsed?.reported?.cid) {
          // Command-completion echo from HA — telemetry only, NOT a device-state update.
          this.analyticsService.trackWidgetInteraction(
            MQTT_TELEMETRY_WIDGET, CommandStage.ACTUATION_CONFIRMED,
            { cid: parsed.reported.cid, entity: entityFromShadowTopic(message.topic), success: !parsed.reported.error, unit: this.unitGuid }
          );
          return;
        }
        if (parsed && parsed.reported?.entity_id) {
          this.storage.setValue(parsed.reported.entity_id, parsed);
          // Also update the cache when we receive updates
          this.deviceCache.updateCachedState(parsed.reported.entity_id, parsed);
        }
      },
      error: (err) => console.error('Error in update subscription:', err)
    });

    // Subscribe to shadow get accepted messages (wildcard subscription)
    const getTopic = `$aws/things/${unit.unit_id}/shadow/name/+/get/accepted`;

    this.client?.observe(getTopic).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (message: IMqttMessage) => {
        const parsed = this.parseShadowGetAccepted(message);
        if (parsed && parsed.state && parsed.state.reported?.entity_id) {
          const entityId = parsed.state.reported.entity_id;
          this.storage.setValue(entityId, parsed.state);
          // Also update the cache with the latest state
          this.deviceCache.updateCachedState(entityId, parsed.state);
        }
      },
      error: (err) => console.error('Error in get/accepted subscription:', err)
    });

    // Subscribe to add-on replies for requestApiCommand (see publishApiCommand
    // for the prefix/authorizer contract). Malformed payloads are logged and
    // dropped rather than allowed to take down this connection handler.
    const apiResponseTopic = `${environment.iotMqttPrefix}/${unit.unit_id}/api/response`;
    console.log('[MQTT] Subscribing to topic:', apiResponseTopic);

    this.client?.observe(apiResponseTopic).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (message: IMqttMessage) => {
        let payload: { response_to?: string; response?: any; error?: string };
        try {
          const convertedPayload = typeof message.payload !== 'string' ? message.payload.toString() : message.payload;
          payload = JSON.parse(convertedPayload);
        } catch (err) {
          console.error('[MQTT] Failed to parse api/response payload:', err);
          return;
        }
        this.handleApiResponse(payload);
      },
      error: (err) => console.error('Error in api/response subscription:', err)
    });
  }

  private parseShadowGetAccepted(message: IMqttMessage): { state: IShadowState } | undefined {
    if (message.payload) {
      const convertedPayload = typeof message.payload !== 'string' ? message.payload.toString() : message.payload;
      return JSON.parse(convertedPayload);
    }
    return undefined;
  }

  /**
   * Request shadow states for hardcoded priority devices that are always expected
   * This ensures quick UI population even before getThings completes
   */
  private requestPriorityDeviceShadows(): void {
    if (!this.isMqttConnected) {
      return;
    }

    // Get hardcoded entity IDs from device registry
    const priorityDevices = [
      ...this.deviceRegistry.getHardcodedEntityIds('lights'),
      ...this.deviceRegistry.getHardcodedEntityIds('climate')
    ];

    // console.log('[MqttConnector] Requesting priority device shadows immediately');

    priorityDevices.forEach(entityId => {
      this.publishShadowGet(entityId).subscribe({
        next: () => {
          // console.log(`[MqttConnector] Requested shadow for priority device: ${entityId}`);
        },
        error: (err) => {
          console.warn(`[MqttConnector] Failed to request shadow for ${entityId}:`, err);
        }
      });
    });
  }

  private requestLatestShadowStates(units?: IUnit[]) {
    if (!units?.length || !this.isMqttConnected) {
      // console.log('[requestLatestShadowStates] Skipping - no units or not connected');
      return;
    }
    
    // Define priority device types that should be updated immediately when loading the homepage
    //
    // This list is the other half of DeviceCacheService's allowlist and the two
    // MUST move together. The cache restores a device's last known state on the
    // first frame; this is what then asks the unit for the truth. Cache an entity
    // without listing it here and its stale value is never corrected — it simply
    // persists. Observed with mood audio: play, hard refresh, stop from another
    // device, and the transport sat showing `playing` indefinitely, because the
    // stop happened while the app was not yet listening and nothing ever asked
    // again.
    const priorityDeviceTypes = [
      'light.',        // All lights 
      'climate.',      // All climate devices
      'alarm_control_panel', // All alarm panels
      'sensor.mood_audio',   // state sensor + the sound library, by prefix
      'media_player.'        // the amplifier the transport bar reads
    ];
    // Exact ids, where a prefix would also catch neighbours (lock.front_door_deadbolt).
    const priorityEntities = [
      FRONT_DOOR_LOCK,       // the front door lock beside the alarm slider
      FRONT_DOOR_LOCK_JAMMED, // and its jam sensor
    ];
    
    let requestCount = 0;
    let skippedCount = 0;
    let deviceCount = 0;
    const requestedDevices: string[] = [];
    
    // Process each unit
    units.forEach(unit => {
      unit.shadowStates?.forEach((state: IDeviceState) => {
        if (state.shadow_name) {
          const entityId = state.shadow_state?.reported?.entity_id ?? state.shadow_name.replace('-', '.');
          deviceCount++;
          
          // Check if this is a priority device that should be updated immediately
          const isPriorityDevice = priorityDeviceTypes.some(prefix => entityId.startsWith(prefix))
            || priorityEntities.includes(entityId);
          
          if (!isPriorityDevice) {
            skippedCount++;
            return; // Skip non-priority devices
          }
          
          requestCount++;
          requestedDevices.push(entityId);
          
          // Simple request without additional tracking
          this.publishShadowGet(entityId).subscribe({
            error: (err) => {} // console.warn(`[requestLatestShadowStates] Failed to request ${entityId}:`, err)
          });
        }
      });
    });
    
    // console.log('[requestLatestShadowStates] Shadow update requests:', {
    //   totalDevices: deviceCount,
    //   requestedCount: requestCount,
    //   skippedCount: skippedCount,
    //   sampleRequested: requestedDevices.slice(0, 10)
    // });
  }

  private initListenersState(units?: IUnit[]) {
    if (units?.length) {
      // console.log('[initListenersState] Initializing state for', units.length, 'units');
      let totalStates = 0;
      let lightStates = 0;
      let climateStates = 0;
      const importantDevices: { [key: string]: any } = {};
      
      units.forEach((unit: IUnit) => {
        unit.shadowStates?.forEach((state: IDeviceState) => {
          if (state.shadow_state?.reported) {
            // Convert shadow_name format: "input_boolean-livingspace_a_circadian" -> "input_boolean.livingspace_a_circadian"
            const entityId = state.shadow_state.reported?.entity_id ?? state.shadow_name.replace('-', '.');
            totalStates++;
            
            if (entityId.includes('light')) lightStates++;
            if (entityId.includes('climate')) climateStates++;
            
            // Log specific important devices
            // if (entityId.includes('livingspace_a') && entityId.includes('circadian')) {
            //   console.log('[initListenersState] Found circadian device:', {
            //     shadowName: state.shadow_name,
            //     entityId: entityId,
            //     state: state.shadow_state.reported?.state,
            //     attributes: state.shadow_state.reported?.attributes
            //   });
            // }
            
            if (entityId.includes('livingspace_a_circadian') || 
                entityId.includes('all_lights') ||
                entityId.includes('livingspace_a_lights') ||
                entityId.includes('climate.livingspace_a')) {
              importantDevices[entityId] = {
                state: state.shadow_state.reported?.state,
                attributes: state.shadow_state.reported?.attributes
              };
            }
            
            this.storage.setValue(entityId, {
              ...state.shadow_state,
              reported: {
                ...state.shadow_state.reported,
                entity_id: entityId
              }
            });
          }
        })
      });
      
      // console.log('[initListenersState] Initialized states:', {
      //   totalStates,
      //   lightStates,
      //   climateStates,
      //   units: units.map(u => u.unit_id),
      //   importantDevices
      // });
    } else {
      // console.log('[initListenersState] No units provided');
    }
  }

  private parseMessage(message: IMqttMessage): IShadowState | undefined {
    if (message.payload) {
      const convertedPayload = typeof message.payload !== 'string' ? message.payload.toString() : message.payload;

      return JSON.parse(convertedPayload)?.state;
    }
    return undefined;
  }

  private getThings(token?: string): Observable<IUnit[] | undefined> {
    if (!token) {
      console.log('[MQTT] getThings: Missing IoT token');
      return of(undefined);
    }

    console.log('[MQTT] getThings: Starting to fetch device data');
    console.log('[MQTT] getThings: Fetching all devices from:', environment.iotThingsEndpoint);

    // Directly call /devices endpoint - it returns the correct unit_id (AWS IoT thing name)
    return this.httpClient.get<IUnit[] | undefined>(environment.iotThingsEndpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    }).pipe(
      tap((response) => {
        console.log('[MQTT] getThings: Got devices response:', {
          unitsCount: response?.length || 0,
          totalDevices: response?.reduce((sum, u) => sum + (u.shadowStates?.length || 0), 0),
        });

        // Set unitGuid from first unit's unit_id (AWS IoT thing name for MQTT topics)
        if (response && response.length > 0) {
          const firstUnit = response[0];
          this.unitGuid = firstUnit.unit_id;
          console.log('[MQTT] getThings: Set unitGuid:', this.unitGuid);
          this.flushPendingCommands(); // deliver commands queued before the unit resolved

          const hasLights = firstUnit.shadowStates?.some(s => s.shadow_name.includes('light'));
          const hasClimate = firstUnit.shadowStates?.some(s => s.shadow_name.includes('climate'));
          console.log('[MQTT] getThings: Device check:', {
            unit: firstUnit.unit_id,
            hasLightDevices: hasLights,
            hasClimateDevices: hasClimate,
            totalShadowStates: firstUnit.shadowStates?.length
          });
        }
      }),
      shareReplay(1),
      catchError((err) => {
        console.error('[MQTT] getThings: Error fetching devices:', err);
        console.error('[MQTT] getThings: Error status:', err.status);
        console.error('[MQTT] getThings: Error message:', err.message);
        if (err.status === 403 || err.status === 401) {
          console.log('[MQTT] getThings: Received auth error, clearing IoT token');
          this.authService.clearIotToken();
        }
        return of(undefined);
      }),
      map((response: IUnit[] | undefined) => {
        console.log('[MQTT] getThings: Returning', response?.length || 0, 'units');
        return response;
      }),
    );
  }

  /**
   * Get all device IDs from the message storage
   * @returns Observable with array of device IDs
   */
  public getAllDeviceIds(): Observable<string[]> {
    return this.storage.items$.pipe(
      map((state: MessagesState) => Object.keys(state))
    );
  }
}
